import { Box, IconButton } from "@mui/material";
import { MenuItem } from "@mui/material";
import { TextField, Typography } from "@mui/material";
import { ChangeEvent, DragEvent, useContext } from "react";
import { useEffect, useRef, useState } from "react";
import { TEXT_COLOR, THEME } from "../../theme";
import { shareModalStyles } from "./styles";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { getBpm } from "../../utils/bpmUtil";
import { AddTrackVersionModalPros } from "./types";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { awaitableUploadToS3 } from "../../utils/S3Utils";
import { RpcError } from "grpc-web";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { postTrack, postTrackVersion } from "../../services/trackService";
import { ContainerModal, styles } from "../../pages/MyTracks/styles";
import {
  AudioMetadata,
  FileState,
  Key,
  KeyNote,
  KeyScale,
  TrackVersion,
  VersionState,
  VersionStateMap,
} from "../../proto/api_pb";
import {
  AuthContext,
  S3CredentialsContextInterface,
  UserDetailsContext,
} from "../../contexts";
import { AuthContextInterface, S3CredentialsContext } from "../../contexts";
import { versionStates } from "./types";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { MAX_CHARACTER } from "../constants";
import {
  getAudioDuration,
  getNextVersionNumber,
} from "../../pages/MyTracks/utils";
import KeyComponent from "../TrackComponents/keyComponent/KeyComponent";
import { CustomLinearProgress } from "../ProgressComponents/CustomLinearProgress";
import CustomButton from "../CustomButton/CustomButton";

const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;
const sanitizeForHeader = (text: string): string => {
  // eslint-disable-next-line no-control-regex
  return text.replace(/[^\x00-\xFF]/g, '');
};

export const AddTrackVersionModal: React.FC<AddTrackVersionModalPros> = ({
  open,
  handleTrackMenuClose,
  handleCloseModal,
  track,
  handleRefreshTracks,
  selectedTrackLatestVersion,
}) => {
  const [file, setFile] = useState<File>();
  const [label, setLabel] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [bpm, setBpm] = useState<number>(0);
  const [key, setKey] = useState("");
  const [scale, setScale] = useState("");
  const [showKeyTooltip, setShowKeyTooltip] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { getValidAccessToken } = useGetValidAccessToken();
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);
  const [versionState, setVersionState] = useState<
    VersionStateMap[keyof VersionStateMap]
  >(VersionState.VERSION_STATE_UNSPECIFIED);
  const [versionNumber, setVersionNumber] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const { isEnterpriseAccount, enterprise } = useContext(UserDetailsContext);
  const audioRef = useRef<HTMLAudioElement>(null);

  const handleKey = (
    event: React.MouseEvent<HTMLElement>,
    newKey: string | null
  ) => {
    if (newKey !== null) {
      setKey(newKey);
    } else {
      setKey("");
    }
  };

  const handleProgress = (pNum: number) => {
    setProgress(pNum);
  };

  const handleScale = (
    event: React.MouseEvent<HTMLElement>,
    newScale: string
  ) => {
    if (newScale !== null) {
      setScale(newScale);
    } else {
      setScale("");
    }
  };

  const handleshowKeyTooltip = () => {
    setShowKeyTooltip((prev) => !prev);
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
        setIsPlaying(true);
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleClose = () => {
    handleTrackMenuClose();
    handleCloseModal && handleCloseModal();
  };

  const handleAddFiles = (
    event: DragEvent<HTMLDivElement> | ChangeEvent<HTMLInputElement>
  ) => {
    if ("dataTransfer" in event) {
      event.preventDefault();
      const newFiles = event.dataTransfer.files[0];
      setFile(newFiles);
    } else if (event.target.files !== null) {
      const files = event.target.files[0];
      if (files !== null) setFile(files);
    }
  };

  useEffect(() => {
    const handleUseEffect = async () => {
      if (file) {
        try {
          const bpmValue = await getBpm(file);
          setBpm(bpmValue);
        } catch (error) {
          console.log(error);
        }
      }
    };
    handleUseEffect();
  }, [file]);

  const sanitizeFileName = (fileName: string): string => {
    return sanitizeForHeader(fileName);
  };

  const handleAddVersion = async () => {
    setLoading(true);
    try {
      if (track && file) {
        let fileToUpload = file;
        const sanitizedFileName = sanitizeFileName(file.name);
        if (sanitizedFileName !== file.name) {
          fileToUpload = new File([file], sanitizedFileName, { type: file.type });
        }
        const songKey = await awaitableUploadToS3(
          userId,
          fileToUpload,
          `song`,
          fileToUpload.type,
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken,
          (progress) => handleProgress(progress)
        );

        const newkey = new Key();
        if (key.length !== 0) {
          if (key.length === 1) newkey.setKey(key[0]);
          if (key[1] === "b") newkey.setNote(KeyNote.KEY_NOTE_FLAT);
          if (key[1] === "#") newkey.setNote(KeyNote.KEY_NOTE_SHARP);
          newkey.setKey(key[0]);
        }
        if (scale === "major") newkey.setScale(KeyScale.KEY_SCALE_MAJOR);
        if (scale === "minor") newkey.setScale(KeyScale.KEY_SCALE_MINOR);

        const songUrl = ASSETS_URL + "/" + songKey;
        const trackVersion = new TrackVersion();
        const audioMetadata = new AudioMetadata();
        audioMetadata.setBpm(bpm);
        audioMetadata.setKey(newkey);
        const sanitizedLabel = sanitizeForHeader(label);
        trackVersion.setLabel(sanitizedLabel);
        audioMetadata.setBpm(bpm);
        audioMetadata.setKey(newkey);
        trackVersion.setAudioMetadata(audioMetadata);
        trackVersion.setTrackUrl(songUrl);
        trackVersion.setTrackId(track.getId()!);
        trackVersion.setFileState(FileState.FILE_STATE_ACTIVE);
        trackVersion.setIsPrimary(true);
        trackVersion.setVersionNumber(versionNumber);
        trackVersion.setVersionState(versionState);
        const songLength = await getAudioDuration(file);
        trackVersion.setLengthSeconds(songLength);
        const postTrackVersionResponse = await postTrackVersion(
          trackVersion,
          await getValidAccessToken(),
          userId,
          isEnterpriseAccount ? enterprise?.id : undefined
        );
        if (postTrackVersionResponse instanceof RpcError) {
          //TODO: handle api exception gracefully with error modal, etc
          throw postTrackVersionResponse;
        }
        track.setTrackVersionId(
          postTrackVersionResponse.getTrackVersion()!.getId()
        );
        const postTrackResponse = await postTrack(
          track,
          await getValidAccessToken(),
          userId
        );
        if (postTrackResponse instanceof RpcError) {
          throw postTrackResponse;
        }
        setLoading(false);
        toast.info("Track version uploaded Successfully");
        await handleRefreshTracks();
        handleClose();
        // Mixpanel.track("track_version_upload_success");
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
      toast.warn("error");
    }
  };

  const handleStatusChange = (event: any) => {
    setVersionState(
      event.target.value as VersionStateMap[keyof VersionStateMap]
    );
  };

  const handleVersionNumberChange = (event: any) => {
    setVersionNumber(event.target.value as number);
  };

  return (
    <ContainerModal
      open={open ? open : false}
      onClose={handleClose}
      style={{ overflow: "auto" }}
    >
      <Box sx={{ ...shareModalStyles.mainContainer, top: "100%" }}>
        <Box sx={shareModalStyles.topBarIconContainer}>
          <Box sx={shareModalStyles.shareIconContainer}>
            <img
              style={shareModalStyles.shareIcons}
              src="/assets/icons/TracksPageIcons/plus.svg"
              alt="icon"
            />
          </Box>
          <Box sx={shareModalStyles.closeIconContainer}>
            <IconButton onClick={handleClose}>
              <CloseIcon sx={{ fontSize: "30px" }} />
            </IconButton>
          </Box>
        </Box>
        <Typography sx={shareModalStyles.title}>Add Version</Typography>
        <Typography sx={styles.uploadAudioTypography}>
          Upload Audio <span className="asterisk">*</span>
        </Typography>
        {!file && (
          <Box
            onDrop={(e) => handleAddFiles(e)}
            sx={shareModalStyles.uploadAudioMainContainer}
          >
            <input
              type="file"
              accept=".mp3, .wav, .ogg"
              style={shareModalStyles.uploadAudioInput}
              onChange={(e) => {
                const files = e.target.files;
                if (!files || files.length === 0) return;
                const file = files[0];
                if (!file.type.includes("audio")) {
                  toast.warning(
                    "Please select a valid audio file (MP3, WAV, OGG)."
                  );
                  return;
                }
                const maxSize = 100 * 1024 * 1024;
                if (file.size > maxSize) {
                  toast.warning(
                    "Selected file exceeds the maximum size of 100MB."
                  );
                  return;
                }
                handleAddFiles(e);
              }}
            />
            <Box sx={shareModalStyles.musicNoteIconContainer}>
              <img
                src="/assets/icons/TracksPageIcons/music-note-02.svg"
                style={{
                  filter: THEME === "dark" ? "invert(0%)" : "invert(100%)",
                }}
                alt="icon"
              />
            </Box>
            <Typography sx={{ color: TEXT_COLOR }}>
              Click to upload or drag and drop
            </Typography>
            <Typography sx={{ color: TEXT_COLOR }}>
              MP3, WAV, OGG Max size 100MB
            </Typography>
          </Box>
        )}
        {file && (
          <Box sx={shareModalStyles.fileDetails}>
            <Typography sx={{ color: TEXT_COLOR }}>
              {file ? sanitizeForHeader(file.name) : ""}
            </Typography>
            <Box>
              <audio
                style={{ display: "none" }}
                ref={audioRef}
                onEnded={() => setIsPlaying(false)}
              >
                <source src={URL.createObjectURL(file)} type={file.type} />
                Your browser does not support the audio tag.
              </audio>
              <IconButton onClick={handlePlayPause}>
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <IconButton
                sx={{ marginLeft: "10px" }}
                onClick={() => setFile(undefined)}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        )}
        <Box sx={{ marginTop: "20px", position: "relative", width: "100%" }}>
          <Typography sx={styles.addNoteTypography}>Add a Note</Typography>
          <textarea
            style={{
              ...styles.modalDescription,
              color: TEXT_COLOR,
              width: "100%",
              padding: 10,
              borderRadius: 5,
              height: 80,
            }}
            value={label}
            placeholder="Add a note for this version"
            maxLength={MAX_CHARACTER}
            onChange={(e) => {
              setLabel(sanitizeForHeader(e.target.value));
              setCharCount(e.target.value.length);
            }}
          ></textarea>
          <div style={styles.noteContainer}>
            {charCount}/{MAX_CHARACTER}
          </div>
        </Box>
        <FormControl sx={{ margin: "20px 0 0" }} fullWidth>
          <label style={{ color: TEXT_COLOR }}>
            Select Version Number <span className="asterisk">*</span>
          </label>
          <Select
            value={
              versionNumber === 0 ? "Specify the version number" : versionNumber
            }
            displayEmpty
            onChange={handleVersionNumberChange}
          >
            {getNextVersionNumber(selectedTrackLatestVersion ?? 0).map(
              (version) => (
                <MenuItem
                  key={version}
                  disabled={version === "Specify the version number"}
                  value={version}
                  sx={{ backgroundColor: "inherit" }}
                >
                  {version}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
        <FormControl sx={{ margin: "25px 0 10px" }} fullWidth>
          <label style={{ color: TEXT_COLOR }}>Select Status</label>
          <Select
            value={versionState}
            displayEmpty
            onChange={handleStatusChange}
          >
            {versionStates.map((el) => {
              return (
                <MenuItem
                  disabled={el.option === "Specify the version state"}
                  key={el.option}
                  value={el.value}
                  sx={{ backgroundColor: "inherit" }}
                >
                  {el.option}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Typography sx={styles.bpmContainer}>
          BPM <span className="optional-input">(optional)</span>
        </Typography>
        <TextField
          placeholder="BPM range 0-200"
          sx={{ width: "100%", mb: 2 }}
          value={bpm}
          onChange={(e) => setBpm(+e.target.value)}
        />
        <KeyComponent
          songKey={key}
          scale={scale}
          showKeyTooltip={showKeyTooltip}
          handleKey={handleKey}
          handleScale={handleScale}
          handleshowKeyTooltip={handleshowKeyTooltip}
          setShowKeyTooltip={setShowKeyTooltip}
        />
        <Box sx={shareModalStyles.buttonsContainer}>
          <CustomButton
            text="Add Version"
            onClick={handleAddVersion}
            isDisabled={file === undefined || versionNumber === 0}
            loading={loading}
            customStyles={{ width: "48%" }}
          />
          <CustomButton
            text="Cancel"
            onClick={handleClose}
            isSkipButton={true}
            customStyles={{ width: "48%" }}
          />
        </Box>
        <Box sx={{ width: "100%", mt: 3, mb: 2 }}>
          {loading && <CustomLinearProgress value={progress} />}
        </Box>
      </Box>
    </ContainerModal>
  );
};
